import baseApi from './baseApi';

export interface AlertThresholds {
  consecutiveErrors: number | null;
  hourlyThreshold: number | null;
  dailyThreshold: number | null;
}

export interface AlertConfig {
  global: AlertThresholds;
  byBusinessUnit: Record<number, Partial<AlertThresholds>>;
}

export const ALERT_DEFAULTS: AlertThresholds = {
  consecutiveErrors: 5,
  hourlyThreshold: 70,
  dailyThreshold: 95,
};

export interface Integration {
  id: number;
  name: string;
  businessUnitIds: number[];
  lastSuccessDate: string | null;
  lastErrorDate: string | null;
  lastStatuses: {
    status: 'success' | 'error';
    businessUnitId: number | null;
    endpoint?: string;
    executionTime?: number;
    createdAt?: string;
  }[];
  alertConfig: AlertConfig | null;
  last24HourStats?: {
    totalExecutions: number;
    successCount: number;
    errorCount: number;
    successRate: number | null;
    statsByBusinessUnit: {
      [businessUnitId: number]: {
        totalExecutions: number;
        successCount: number;
        errorCount: number;
        successRate: number | null;
      };
    };
  };
}

export interface IntegrationExecution {
  id: number;
  integrationId: number;
  businessUnitId: number | null;
  endpoint: string;
  clientId: number | null;
  type: 'http' | 'function';
  method: string | null;
  input: unknown;
  output: unknown;
  status: 'success' | 'error';
  statusCode: number | null;
  error: string | null;
  executionTime: number;
  createdAt: string;
  updatedAt: string;
}

export interface IntegrationExecutionDetail extends IntegrationExecution {
  headers: unknown;
}

export interface IntegrationExecutionsResponse {
  executions: IntegrationExecution[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface IntegrationErrorGroup {
  statusCode: number | null;
  endpoint: string;
  error: string | null;
  count: number;
  firstSeen: string;
  lastSeen: string;
}

export interface IntegrationErrorSummaryResponse {
  errors: IntegrationErrorGroup[];
}

export interface IntegrationStats {
  totalExecutions: number;
  successCount: number;
  errorCount: number;
  successRate: number | null;
}

const integrationsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getIntegrations: build.query<Integration[], void>({
      query: () => '/admin/integrations',
    }),
    updateIntegrationAlertConfig: build.mutation<
      { alertConfig: AlertConfig | null },
      { id: number; alertConfig: AlertConfig | null }
    >({
      query: ({ id, alertConfig }) => ({
        url: `/admin/integrations/${id}/alertConfig`,
        method: 'PATCH',
        body: { alertConfig },
      }),
    }),
    getIntegrationExecutions: build.query<
      IntegrationExecutionsResponse,
      {
        id: number;
        page?: number;
        limit?: number;
        status?: 'success' | 'error';
        statusCode?: number;
        startDate?: string;
        endDate?: string;
        businessUnitId?: number;
        search?: string;
      }
    >({
      query: ({ id, page = 1, limit = 50, status, statusCode, startDate, endDate, businessUnitId, search }) => {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        if (status) params.append('status', status);
        if (statusCode !== undefined) params.append('statusCode', statusCode.toString());
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (businessUnitId !== undefined) params.append('businessUnitId', businessUnitId.toString());
        if (search) params.append('search', search);

        return `/admin/integrations/${id}/executions?${params.toString()}`;
      },
    }),
    getIntegrationExecutionDetail: build.query<IntegrationExecutionDetail, { id: number; executionId: number }>({
      query: ({ id, executionId }) => `/admin/integrations/${id}/executions/${executionId}`,
    }),
    getIntegrationErrorSummary: build.query<
      IntegrationErrorSummaryResponse,
      { id: number; startDate?: string; endDate?: string; businessUnitId?: number; limit?: number }
    >({
      query: ({ id, startDate, endDate, businessUnitId, limit }) => {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (businessUnitId !== undefined) params.append('businessUnitId', businessUnitId.toString());
        if (limit !== undefined) params.append('limit', limit.toString());

        return `/admin/integrations/${id}/error-summary?${params.toString()}`;
      },
    }),
    getIntegrationStats: build.query<
      IntegrationStats,
      { id: number; startDate?: string; endDate?: string; businessUnitId?: number }
    >({
      query: ({ id, startDate, endDate, businessUnitId }) => {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (businessUnitId !== undefined) params.append('businessUnitId', businessUnitId.toString());

        return `/admin/integrations/${id}/stats?${params.toString()}`;
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetIntegrationsQuery,
  useGetIntegrationExecutionsQuery,
  useGetIntegrationExecutionDetailQuery,
  useGetIntegrationErrorSummaryQuery,
  useGetIntegrationStatsQuery,
  useUpdateIntegrationAlertConfigMutation,
} = integrationsApi;
